import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectDto } from "@reviewer73/shared";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api/client.js";
import { EmptyNote } from "../components/empty-note.js";
import { Rack } from "../components/rack.js";
import { StaggerContainer, StaggerItem } from "../components/stagger.js";
import { Strip, StripField, StripFields } from "../components/strip.js";
import { Input } from "../components/ui/input.js";
import { Switch } from "../components/ui/switch.js";
import { ModelPicker } from "../components/model-picker.js";
import { GroupsEditor } from "../components/groups-editor.js";
import { repoName } from "../lib/format.js";

export function Projects() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");
  const { data, isLoading, error } = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const rules = useQuery({ queryKey: ["rules"], queryFn: api.rules });
  const knownGroups = [...new Set((rules.data?.rules ?? []).map((r) => r.group))];

  const update = useMutation({
    mutationFn: (vars: { id: string; body: Parameters<typeof api.updateProject>[1] }) =>
      api.updateProject(vars.id, vars.body),
    // Optimistic: the switch flips instantly with its spring, the network
    // confirms silently after. A toast only appears if it actually fails.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["projects"] });
      const previous = queryClient.getQueryData<ProjectDto[]>(["projects"]);
      queryClient.setQueryData<ProjectDto[]>(["projects"], (old) =>
        old?.map((p) => (p.id === vars.id ? { ...p, ...vars.body } : p)),
      );
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      queryClient.setQueryData(["projects"], ctx?.previous);
      toast.error("Änderung fehlgeschlagen", { description: (err as Error).message });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  if (error) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">
        Projekte konnten nicht geladen werden.
      </div>
    );
  }

  const visible = (data ?? []).filter((p) =>
    p.pathWithNamespace.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-baseline gap-2 text-xl font-bold tracking-tight">
          Projekte
          {!isLoading ? (
            <span className="text-sm font-normal tabular-nums text-muted-foreground">
              {visible.length}
            </span>
          ) : null}
        </h1>
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filtern…"
          className="w-48"
        />
      </div>

      <p className="max-w-prose text-sm text-muted-foreground">
        Projekte erscheinen hier, sobald in ihnen der erste Merge Request geöffnet wird. Das
        Ziel-Repository selbst braucht keine Konfiguration.
      </p>

      <Rack trayClassName="space-y-2">
        {!isLoading && visible.length === 0 ? (
          <EmptyNote>Kein Projekt passt zum Filter.</EmptyNote>
        ) : (
          <StaggerContainer className="space-y-2">
            {visible.map((project) => (
              <StaggerItem key={project.id}>
                {/* A project strip: enabled ones wear the live-green holder,
                    disabled ones the gray. The switch is the bay's own. */}
                <Strip
                  holderColor={project.enabled ? "var(--hold-running)" : "var(--hold-skipped)"}
                  className="tile--lift cursor-pointer"
                  // The whole row opens the project; clicks on the row's own
                  // controls (switch, model select) must not navigate. The
                  // name link stays as the keyboard/screen-reader path.
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a, button, [role='combobox'], [role='listbox']")) {
                      return;
                    }
                    navigate(`/projects/${project.id}`);
                  }}
                >
                  <StripFields>
                    <StripField label="Aktiv" className="w-20 items-start">
                      <Switch
                        checked={project.enabled}
                        disabled={update.isPending}
                        onCheckedChange={(enabled) => update.mutate({ id: project.id, body: { enabled } })}
                        aria-label={`Reviews für ${repoName(project.pathWithNamespace)}`}
                      />
                    </StripField>
                    <StripField label="Projekt" className="w-[28%] min-w-44">
                      <Link
                        to={`/projects/${project.id}`}
                        className="truncate font-bold text-primary hover:underline"
                      >
                        {repoName(project.pathWithNamespace)}
                      </Link>
                    </StripField>
                    <StripField label="Modell" className="w-[26%] min-w-56">
                      <ModelPicker
                        value={project.model}
                        onChange={(model) => update.mutate({ id: project.id, body: { model } })}
                        className="max-w-56"
                      />
                    </StripField>
                    <StripField
                      label="Gruppen"
                      className="hidden flex-1 md:flex"
                      valueClassName="overflow-visible whitespace-normal"
                    >
                      <GroupsEditor
                        groups={project.groups}
                        knownGroups={knownGroups}
                        onChange={(groups) => update.mutate({ id: project.id, body: { groups } })}
                      />
                    </StripField>
                    <StripField label="Reviews" className="w-20">
                      <span className="font-mono tabular-nums">{project.reviewCount}</span>
                    </StripField>
                    <StripField label="Findings" className="w-20">
                      <span className="font-mono tabular-nums">{project.findingCount}</span>
                    </StripField>
                  </StripFields>
                </Strip>
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}
      </Rack>
    </div>
  );
}
